import { useState, useCallback, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

// ----------------------------------------------------------------
// State machine — onboarding para usuários convidados (sem signup)
// ----------------------------------------------------------------
export type OnboardingStatus =
  | 'welcome'
  | 'highlights'
  | 'config'
  | 'confirming'
  | 'provisioning'
  | 'completed'
  | 'failed';

export interface OnboardingState {
  // Step 3 — Configuração inicial
  empresa_nome: string;
  cnpj: string;
  segmento: string;
  fuso_horario: string;
  qg_alert_days: number;
  medicao_alert_days: number;
  // Meta
  currentStep: number; // 1-4
  status: OnboardingStatus;
  idempotency_key: string;
  // Dados do usuário autenticado (preenchidos automaticamente)
  user_name: string;
  user_email: string;
}

export function useOnboarding() {
  const [state, setState] = useState<OnboardingState>({
    empresa_nome: '',
    cnpj: '',
    segmento: '',
    fuso_horario: 'America/Sao_Paulo',
    qg_alert_days: 5,
    medicao_alert_days: 10,
    currentStep: 1,
    status: 'welcome',
    idempotency_key: crypto.randomUUID(),
    user_name: '',
    user_email: '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [authenticated, setAuthenticated] = useState<boolean | null>(null);

  // Verificar sessão ao montar — o usuário já chegou via convite
  useEffect(() => {
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        setAuthenticated(true);
        setState(prev => ({
          ...prev,
          user_name: session.user.user_metadata?.name || session.user.user_metadata?.full_name || '',
          user_email: session.user.email || '',
        }));
      } else {
        setAuthenticated(false);
      }
    };
    checkSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        setAuthenticated(true);
        setState(prev => ({
          ...prev,
          user_name: session.user.user_metadata?.name || session.user.user_metadata?.full_name || '',
          user_email: session.user.email || '',
        }));
      } else {
        setAuthenticated(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const update = useCallback((patch: Partial<OnboardingState>) => {
    setState(prev => ({ ...prev, ...patch }));
    setError(null);
  }, []);

  const nextStep = useCallback(() => {
    setState(prev => ({
      ...prev,
      currentStep: Math.min(prev.currentStep + 1, 4),
    }));
  }, []);

  const prevStep = useCallback(() => {
    setState(prev => ({
      ...prev,
      currentStep: Math.max(prev.currentStep - 1, 1),
    }));
  }, []);

  // ── Step 4: provisionamento via Edge Function ─────────────────
  const provision = useCallback(async (): Promise<{ success: boolean; cluster_id?: string }> => {
    setLoading(true);
    setError(null);
    setState(prev => ({ ...prev, status: 'provisioning' }));

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Sessão não encontrada. Faça login novamente.');

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/onboarding-create-tenant`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            idempotency_key: state.idempotency_key,
            empresa_nome: state.empresa_nome,
            cnpj: state.cnpj || null,
            segmento: state.segmento || null,
            configuracoes_iniciais: {
              fuso_horario: state.fuso_horario,
              qg_alert_days: state.qg_alert_days,
              medicao_alert_days: state.medicao_alert_days,
            },
          }),
        }
      );

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Falha no provisionamento');
      }

      setState(prev => ({ ...prev, status: 'completed' }));
      return { success: true, cluster_id: result.cluster_id };
    } catch (err: any) {
      setState(prev => ({ ...prev, status: 'failed' }));
      setError(err.message);
      return { success: false };
    } finally {
      setLoading(false);
    }
  }, [state]);

  // ── Validação de CNPJ ────────────────────────────────────────
  const validateCnpj = useCallback((cnpj: string): boolean => {
    if (!cnpj) return true;
    const clean = cnpj.replace(/\D/g, '');
    if (clean.length !== 14) return false;
    if (/^(\d)\1+$/.test(clean)) return false;
    const calc = (str: string, weights: number[]) =>
      weights.reduce((sum, w, i) => sum + parseInt(str[i]) * w, 0);
    const mod = (n: number) => { const r = n % 11; return r < 2 ? 0 : 11 - r; };
    const w1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    const w2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    return (
      mod(calc(clean, w1)) === parseInt(clean[12]) &&
      mod(calc(clean, w2)) === parseInt(clean[13])
    );
  }, []);

  return {
    state,
    loading,
    error,
    authenticated,
    update,
    nextStep,
    prevStep,
    provision,
    validateCnpj,
    setError,
  };
}
